import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {DEFAULT_SEGMENT, deriveQualityFurnishingsContinuous, hasSpfTable} from "@reva/shared/parameter-resolution";
import type {RevaValueParameterType} from "@reva/shared/reva-spec";
import {isRevaSegment, listSegmentCandidates} from "@reva/shared/segment-description";
import type {InitialValuationAdditionalProperties, RevaSegment} from "@reva/shared/valuation-data";

import type {ValueIndicationExternalValidationErrors} from "../api/payload-types/value-indication";
import {readExposeSegment} from "../util/immodropSource";
import {appendExternalValidationErrors} from "../util/objectParameterValidation";

export interface ObjectParamError {
    /** error message (DE) */
    error_de?: string | null;
    /** error message (EN) */
    error_en?: string | null;
    /** translation key for i18n error message */
    languageKey?: string | null;
    /** optional parameter for i18n error message */
    languageParams?: Record<string, any> | null;
    /** optional array of relevant parameters, if this is combined a validation error */
    dependentParams?: string[] | null;
}

export interface ObjectParameterValues {
    reva: string | null;
    avm: string | null;
    initialValuation: string | null;
    inspection: string | null;
    inspectionDerived: string | null;
    plot: string | null;
    energy: string | null;
    register: string | null;
    firstFinalize: string | null;
    comment: string | null;
    /** flag indicating if this function returns a conflicting value */
    useConflictingValue: boolean;
}

export type ObjectParamErrorsMap = Record<string, ObjectParamError[]>;

// #19104: "Benutz- und Verwertbarkeit anpassen" soll standardmäßig ausgeklappt sein
const DEFAULT_META_PARAMS = {meta_usability: true};

/**
 * Parametertyp, dessen Schicht als Ganzes überschrieben werden darf. META und FIRST_FINALIZE sind
 * ebenfalls Persistenzschichten, werden aber nie sammelweise geschrieben: META hält nur GUI-Zustand,
 * FIRST_FINALIZE ist der eingefrorene Stand der ersten Finalisierung und damit nur lesbar.
 */
export type BulkWritableParameterType = Exclude<RevaValueParameterType, "META" | "FIRST_FINALIZE">;

/** Persistenzschicht → Name des zugehörigen Feldes im State, für die Sammel-Reducer. */
const STATE_KEY_BY_PARAMETER_TYPE = {
    DOC_PROPERTY: "properties",
    ENERGY_CERT: "energy",
    IDP_ENERGY_CERTIFICATE: "idpEnergyCertificate",
    REGISTER: "register",
    INSPECTION_DERIVED: "inspectionDerived",
    PLOT: "plot",
    IMMODROP: "immodrop",
    AVM: "params",
} as const satisfies Record<BulkWritableParameterType, keyof ObjectParametersState>;

/** State-Felder, die eine sammelweise beschreibbare Parameter-Schicht halten. */
type ParameterLayerKey = (typeof STATE_KEY_BY_PARAMETER_TYPE)[BulkWritableParameterType];

/** Payload der Sammel-Reducer: eine ganze Schicht mit ihren Parameterwerten. */
export interface BulkWriteParameterLayerPayload {
    parameterType: BulkWritableParameterType;
    params: Record<string, any>;
}

/** Payload zum Schreiben eines einzelnen Parameterwertes in seine Schicht. */
export interface SingleParamPayload {
    parameter: string;
    parameterType: RevaValueParameterType;
    /** Spec-getriebener Wert, daher untypisiert; null/undefined löschen den Eintrag, "empty" bleibt stehen. */
    value: any;
}

/** Payload zum Schreiben eines einzelnen Parameter-Kommentars in seine Schicht. */
export interface SingleCommentPayload {
    parameter: string;
    parameterType: RevaValueParameterType;
    /** null/undefined löschen den Kommentar. */
    comment: string | null | undefined;
}

/** Payload mit dem Ergebnis eines vollständigen Validierungslaufs. */
export interface ParamErrorsPayload {
    errors?: ObjectParamErrorsMap | null;
}

/** Payload mit den Beanstandungen der AVM aus einer gescheiterten Indikation. */
export interface ExternalValidationErrorsPayload {
    externalValidationErrors: ValueIndicationExternalValidationErrors;
}

/**
 * Payload mit den Schichten einer Bewertungsantwort, die der Benutzer bearbeiten kann. Nur `params`
 * ist Pflicht: Die REVA-Schicht schickt der Server immer mit, die übrigen lässt er weg, solange sie
 * leer sind (siehe ValuationData).
 */
export interface SegmentAndParametersPayload {
    /** Vom Backend berechnetes effektives Segment; fehlt, solange die Bewertung noch nicht angelegt ist. */
    segment?: RevaSegment;
    params: Record<string, any>;
    comments?: Record<string, string>;
    properties?: Record<string, any>;
    plot?: Record<string, any>;
    plotComments?: Record<string, string>;
    inspectionDerived?: Record<string, any>;
    inspectionDerivedComments?: Record<string, string>;
    energy?: Record<string, any>;
    energyComments?: Record<string, string>;
    idpEnergyCertificate?: Record<string, any>;
    register?: Record<string, any>;
    registerComments?: Record<string, string>;
    immodrop?: Record<string, any>;
}

/**
 * Payload mit den nur lesbaren Schichten einer Bewertungsantwort. Alle Felder sind optional, weil
 * die Antwort eine Schicht weglässt, solange sie leer ist (siehe ValuationData).
 */
export interface InitialValuationAndInspectionParamsPayload {
    initialValuationParams?: Record<string, any>;
    inspectionParams?: Record<string, any>;
    initialValuationOutputParams?: Record<string, any>;
    initialValuationAdditionalProperties?: InitialValuationAdditionalProperties;
    firstFinalizeParams?: Record<string, any>;
}

/**
 * Löscht die gespeicherten Validierungsfehler eines Parameters, samt der kombinierten Fehler, die ihn
 * mitnennen.
 *
 * Ein geänderter Wert macht seine bisherigen Fehler ungültig; was danach wirklich noch falsch ist,
 * trägt der folgende `validateFormThunk()` neu ein. Nötig, weil `setParamErrors` nur mergen kann und
 * damit von sich aus nie etwas abräumt (#19159).
 * @param errorMap Fehler-Map aus dem State (wird in place geändert)
 * @param parameter Name des geänderten Parameters
 */
const clearErrorsForParameter = (errorMap: ObjectParamErrorsMap, parameter: string) => {
    const errors = errorMap[parameter];
    if (Array.isArray(errors)) {
        for (const errorItem of errors) {
            for (const dependentParam of errorItem.dependentParams ?? []) {
                errorMap[dependentParam] = (errorMap[dependentParam] ?? []).filter(
                    (item) => !(item.dependentParams || []).includes(parameter),
                );
            }
        }
    }
    errorMap[parameter] = [];
};

interface ObjectParametersState {
    initialValuationParams: Record<string, any>;
    initialValuationOutputParams: Record<string, any>;
    inspectionParams: Record<string, any>;
    initialValuationAdditionalProperties: Record<string, any>;
    firstFinalizeParams: Record<string, any>;

    inspectionDerived: Record<string, any>;
    inspectionDerivedComments: Record<string, string>;
    metaParams: Record<string, any>;
    /** REVA-Overrides (Tab Überblick); ein `segment` darin ist der Segment-Override */
    params: Record<string, any>;
    /**
     * Das vom Backend berechnete effektive Segment (#19444). Kein eigenständig setzbarer Wert: es
     * folgt aus der Kette Override → Erstbewertung → Besichtigung → Exposé → Anker, die allein im
     * Backend lebt. Null, solange keine Antwort vorliegt (Bewertung noch nicht angelegt).
     */
    effectiveSegment: string | null;
    comments: Record<string, string>;
    plot: Record<string, any>;
    plotComments: Record<string, string>;
    /** properties are only for documentational purposes, they are not used in any indication */
    properties: Record<string, any>;
    /** energy parameter are parameters related to energy indication */
    energy: Record<string, any>;
    energyComments: Record<string, string>;
    /** energy certificate parameters extracted via IDP (fallback beneath "energy") */
    idpEnergyCertificate: Record<string, any>;
    /** register parameter are parameters related to register entries */
    register: Record<string, any>;
    registerComments: Record<string, string>;
    /** raw extraction result from the last Immodrop URL/PDF scan */
    immodrop: Record<string, any>;
    paramErrors: ObjectParamErrorsMap;
    editedValueParamErrors: ObjectParamError[] | null;
    /** Array von Konflikt-Parametern */
    parameterConflicts: string[];
}

const initialState: ObjectParametersState = {
    initialValuationParams: {},
    initialValuationOutputParams: {},
    inspectionParams: {},
    initialValuationAdditionalProperties: {},
    firstFinalizeParams: {},

    inspectionDerived: {},
    inspectionDerivedComments: {},
    metaParams: {...DEFAULT_META_PARAMS},
    params: {},
    effectiveSegment: null,
    comments: {},
    plot: {},
    plotComments: {},
    properties: {},
    energy: {},
    energyComments: {},
    idpEnergyCertificate: {},
    register: {},
    registerComments: {},
    immodrop: {},
    paramErrors: {},
    editedValueParamErrors: null,
    parameterConflicts: [],
};

export const objectParametersSlice = createSlice({
    name: "objectParameters",
    initialState,
    reducers: {
        resetObjectParameters: (state) => {
            state.params = {};
            state.effectiveSegment = null;
            state.paramErrors = {};
            state.metaParams = {...DEFAULT_META_PARAMS};
            state.initialValuationParams = {};
            state.initialValuationOutputParams = {};
            state.inspectionParams = {};
            state.firstFinalizeParams = {};
            state.properties = {};
            state.energy = {};
            state.idpEnergyCertificate = {};
            state.register = {};
            state.inspectionDerived = {};
            state.plot = {};
            state.immodrop = {};
            state.comments = {};
        },
        // Ersetzt eine ganze Schicht. Die Fehler aller beteiligten Parameter (alte wie neue Belegung)
        // werden verworfen, weil sich ihr wirksamer Wert geändert haben kann — genau wie beim
        // Einzel-Update (#19159).
        setParamsForParameterType: (state, action: PayloadAction<BulkWriteParameterLayerPayload>) => {
            const {parameterType, params} = action.payload;
            // Für TypeScript immer belegt; der Guard fängt die noch untypisierten JS-Aufrufer ab.
            const stateKey: ParameterLayerKey | undefined = STATE_KEY_BY_PARAMETER_TYPE[parameterType];
            if (!stateKey) {
                console.warn("setParamsForParameterType: Unknown parameter type", parameterType);
                return;
            }
            const changedParameters = new Set([...Object.keys(state[stateKey] ?? {}), ...Object.keys(params ?? {})]);
            state[stateKey] = params;
            for (const parameter of changedParameters) {
                if (state.paramErrors[parameter]?.length) clearErrorsForParameter(state.paramErrors, parameter);
            }
        },
        updateParamsForParameterType: (state, action: PayloadAction<BulkWriteParameterLayerPayload>) => {
            const {parameterType, params} = action.payload;
            // Für TypeScript immer belegt; der Guard fängt die noch untypisierten JS-Aufrufer ab.
            const stateKey: ParameterLayerKey | undefined = STATE_KEY_BY_PARAMETER_TYPE[parameterType];
            if (!stateKey) {
                console.warn("updateParamsForParameterType: Unknown parameter type", parameterType);
                return;
            }
            state[stateKey] = {...(state[stateKey] ?? {}), ...params};
            for (const parameter of Object.keys(params ?? {})) {
                if (state.paramErrors[parameter]?.length) clearErrorsForParameter(state.paramErrors, parameter);
            }
        },
        setSingleParam: (state, action: PayloadAction<SingleParamPayload>) => {
            const {value, parameter, parameterType} = action.payload;
            let valueMap: Record<string, any> = {};
            switch (parameterType) {
                case "META":
                    valueMap = state.metaParams;
                    break;
                case "DOC_PROPERTY":
                    valueMap = state.properties;
                    break;
                case "ENERGY_CERT":
                    valueMap = state.energy;
                    break;
                case "IDP_ENERGY_CERTIFICATE":
                    valueMap = state.idpEnergyCertificate;
                    break;
                case "REGISTER":
                    valueMap = state.register;
                    break;
                case "PLOT":
                    valueMap = state.plot;
                    break;
                case "INSPECTION_DERIVED":
                    valueMap = state.inspectionDerived;
                    break;
                case "AVM":
                    valueMap = state.params;
                    break;
                default:
                    console.warn(`setSingleParam: Unknown parameter type for parameter ${parameter}`, parameterType);
                    break;
            }

            if (typeof value === "undefined" || value === null) {
                delete valueMap[parameter];
            } else {
                // 'empty' ist hier explizit drin!
                valueMap[parameter] = value;
            }

            clearErrorsForParameter(state.paramErrors, parameter);
        },
        // Beanstandungen der AVM aus einer gescheiterten Indikation. Sie leben neben der eigenen
        // Validierung in derselben Map und werden nur beim Schreiben eines der benannten Parameter
        // wieder abgeräumt — Lebensdauer und Begründung siehe appendExternalValidationErrors.
        setExternalValidationErrors: (state, action: PayloadAction<ExternalValidationErrorsPayload>) => {
            state.paramErrors = appendExternalValidationErrors(
                state.paramErrors,
                action.payload.externalValidationErrors,
            );
        },
        // Ergebnis eines vollständigen Validierungslaufs. Bewusst mergend statt ersetzend: die
        // externen AVM-Fehler liegen in derselben Map und würden sonst bei jedem Lauf verschwinden.
        // Abgeräumt wird deshalb ausschließlich parameterweise beim Schreiben eines Wertes
        // (clearErrorsForParameter) — ein Validierungslauf allein kann keinen Fehler entfernen (#19159).
        setParamErrors: (state, action: PayloadAction<ParamErrorsPayload>) => {
            const {errors} = action.payload;
            const errorMap = state.paramErrors;

            if (errors) {
                state.paramErrors = mergeErrors(errorMap, errors);
            }
        },
        /**
         * Übernimmt eine Bewertungsantwort in die Parameterschichten. `segment` ist das vom Backend
         * berechnete effektive Segment; der Segment-Override reist als `params.segment` in der
         * REVA-Schicht mit (#19444). Vor dem Anlegen gibt es noch kein effektives Segment — dann
         * bleibt `segment` weg und nur der Override steht (siehe importThunk).
         */
        setSegmentAndParameters: (state, action: PayloadAction<SegmentAndParametersPayload>) => {
            const {
                segment,
                params,
                comments,
                properties,
                inspectionDerived,
                inspectionDerivedComments,

                plot,
                plotComments,
                energy,
                energyComments,
                idpEnergyCertificate,
                register,
                registerComments,
                immodrop,
            } = action.payload;
            state.params = {...params};
            state.effectiveSegment = segment ?? null;
            state.comments = {...(comments ?? {})};

            state.energy = {...(energy ?? {})};
            state.energyComments = {...(energyComments ?? {})};
            state.idpEnergyCertificate = {...(idpEnergyCertificate ?? {})};

            state.register = {...(register ?? {})};
            state.registerComments = {...(registerComments ?? {})};

            state.immodrop = {...(immodrop ?? {})};

            state.inspectionDerived = {...(inspectionDerived ?? {})};
            state.inspectionDerivedComments = {...(inspectionDerivedComments ?? {})};

            state.plot = {...(plot ?? {})};
            state.plotComments = {...(plotComments ?? {})};

            state.properties = {...(properties ?? {})};

            state.paramErrors = {};
            state.metaParams = {...DEFAULT_META_PARAMS};
        },
        setInitialValuationAndInspectionParams: (
            state,
            action: PayloadAction<InitialValuationAndInspectionParamsPayload>,
        ) => {
            const {
                initialValuationParams,
                inspectionParams,
                initialValuationOutputParams,
                initialValuationAdditionalProperties,
                firstFinalizeParams,
            } = action.payload;
            state.initialValuationParams = initialValuationParams ? {...initialValuationParams} : {};
            //Sonderfall: Um Konflikte zwischen Erstbewertung und Besichtigung für diesen AVM relevanten Parameter darzustellen
            //wollen wir bei gegebenen qualitiy_furnishings Parameter diesen Wert auch für quality_furnishings_continuous verwenden.
            deriveQualityFurnishingsContinuous(state.initialValuationParams);

            state.inspectionParams = inspectionParams ?? {};
            // #17915 Übernehme initiale Swot Einträge aus dem Output des BesiConverters
            const inspection_swot_positives = inspectionParams?.swot_features_positive
                ? {items: inspectionParams.swot_features_positive.map((e: string) => ({description: e}))}
                : {items: []};
            const inspection_swot_negatives = inspectionParams?.swot_features_negative
                ? {items: inspectionParams.swot_features_negative.map((e: string) => ({description: e}))}
                : {items: []};
            state.properties = {inspection_swot_positives, inspection_swot_negatives, ...(state.properties ?? {})};
            state.initialValuationOutputParams = initialValuationOutputParams ?? {};
            state.initialValuationAdditionalProperties = initialValuationAdditionalProperties ?? {};
            state.firstFinalizeParams = {...(firstFinalizeParams ?? {})};
        },
        setSingleComment: (state, action: PayloadAction<SingleCommentPayload>) => {
            const {parameter, parameterType, comment} = action.payload;
            let valueMap: Record<string, string>;
            switch (parameterType) {
                case "AVM":
                    valueMap = state.comments;
                    break;
                case "ENERGY_CERT":
                    valueMap = state.energyComments;
                    break;
                case "REGISTER":
                    valueMap = state.registerComments;
                    break;
                case "INSPECTION_DERIVED":
                    valueMap = state.inspectionDerivedComments;
                    break;
                case "PLOT":
                    valueMap = state.plotComments;
                    break;
                default:
                    console.warn(`setSingleComment: parameterType is ${parameterType}, but parameter is ${parameter}`);
                    return;
            }
            if (typeof comment === "undefined" || comment === null) {
                delete valueMap[parameter];
            } else {
                valueMap[parameter] = comment;
            }
        },
        setEditedValueParamErrors: (state, action: PayloadAction<{errors: ObjectParamError[] | null} | null>) => {
            state.editedValueParamErrors = action.payload ? action.payload?.errors : null;
        },
        setParameterConflict: (state, action: PayloadAction<{parameter: string}>) => {
            //console.log("setParameterConflict: state: ", state, "action: ", action);
            const parameter = action.payload.parameter;
            const existingConflictIndex = state.parameterConflicts.indexOf(parameter);

            if (existingConflictIndex < 0) {
                //console.log("setParameterConflict: Adding conflict for parameter", parameter, "at index", existingConflictIndex);
                // Neuen Parameter zur Konfliktliste hinzufügen
                state.parameterConflicts.push(parameter);
            }
        },
        clearAllParameterConflicts: (state) => {
            state.parameterConflicts = [];
            //console.log("clearAllParameterConflicts: Cleared all parameters");
        },
    },
    selectors: {
        /**
         * Das wirksame Segment der Bewertung. Das Backend liefert es berechnet mit (#19444).
         *
         * Der Override steht bewusst VOR dem effektiven Segment: er ist das stärkste Glied der Kette,
         * liegt also einer vor, ist das effektive Segment per Definition derselbe Wert. Diese
         * Reihenfolge deckt zusätzlich die beiden Zustände ab, in denen es noch kein effektives
         * Segment gibt — die Bewertung vor dem Anlegen und die gerade gewählte, noch nicht bestätigte
         * Auswahl während eines laufenden Schreibvorgangs.
         */
        selectSegment: (state) => state.params?.segment ?? state.effectiveSegment ?? DEFAULT_SEGMENT,
        selectAllParams: (state) => state.params,
        selectAllMetaParams: (state) => state.metaParams,
        selectInitialValuationOutputParams: (state) => state.initialValuationOutputParams,
        selectAllInitialValuationParams: (state) => state.initialValuationParams,
        selectInitialValuationAdditionalProperties: (state) => state.initialValuationAdditionalProperties,
        selectAllInspectionParams: (state) => state.inspectionParams,
        selectAllFirstFinalizeParams: (state) => state.firstFinalizeParams,
        selectAllInspectionDerivedParams: (state) => state.inspectionDerived,
        selectAllPlotParams: (state) => state.plot,
        selectAllDocProperties: (state) => state.properties,
        selectAllEnergyParams: (state) => state.energy,
        selectAllIdpEnergyCertParams: (state) => state.idpEnergyCertificate,
        selectAllRegisterParams: (state) => state.register,
        selectAllImmodropParams: (state) => state.immodrop,
        selectAllParamErrors: (state) => state.paramErrors,
        selectEditedValueParamErrors: (state) => state.editedValueParamErrors,
        selectHasSpfTable: (state) => hasSpfTable(state.inspectionDerived),
        /**
         * Select single parameter or parameter details value
         * @param state redux state
         * @param parameter parameter name to query
         * @param parameterType parameter type selecting the layer
         * @return resulting parameter value
         */
        selectSingleParam: (state, parameter: string, parameterType: RevaValueParameterType = "AVM") => {
            switch (parameterType) {
                case "AVM":
                    return state.params[parameter] ?? null;
                case "META":
                    return state.metaParams[parameter] ?? null;
                case "DOC_PROPERTY":
                    return state.properties[parameter] ?? null;
                case "ENERGY_CERT":
                    return state.energy[parameter] ?? null;
                case "IDP_ENERGY_CERTIFICATE":
                    return state.idpEnergyCertificate[parameter] ?? null;
                case "REGISTER":
                    return state.register[parameter] ?? null;
                case "INSPECTION_DERIVED":
                    return state.inspectionDerived[parameter] ?? null;
                case "PLOT":
                    return state.plot[parameter] ?? null;
                case "IMMODROP":
                    return state.immodrop[parameter] ?? null;
                default:
                    return state.params[parameter] ?? null;
            }
        },

        /**
         * Select single initial valuation parameter value (only if parameterType is "AVM" or undefined).
         * @param state redux state
         * @param parameter initial valuation parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleInitialValuationParam: (
            state,
            parameter: string,
            parameterType: RevaValueParameterType = "AVM",
        ) => {
            if (parameterType !== "AVM") return null;
            return state.initialValuationParams?.[parameter] ?? null;
        },

        /**
         * Select single AVM parameter value (only if parameterType is "AVM" or undefined).
         * @param state redux state
         * @param parameter AVM parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleAvmParam: (state, parameter: string, parameterType: RevaValueParameterType = "AVM") => {
            if (parameterType !== "AVM") return null;
            return state.params?.[parameter] ?? null;
        },

        /**
         * Select single Meta parameter value (only if parameterType is "META" or undefined).
         * @param state redux state
         * @param parameter Meta parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleMetaParam: (state, parameter: string, parameterType: RevaValueParameterType = "META") => {
            if (parameterType !== "META") return null;
            return state.metaParams?.[parameter] ?? null;
        },

        /**
         * Select single energy certificate parameter value (only if parameterType is "ENERGY_CERT" or undefined).
         * @param state redux state
         * @param parameter Energy Cert parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleEnergyParam: (state, parameter: string, parameterType: RevaValueParameterType = "ENERGY_CERT") => {
            if (parameterType !== "ENERGY_CERT") return null;
            return state.energy?.[parameter] ?? null;
        },

        /**
         * Select single IDP energy certificate parameter value (only if parameterType is "IDP_ENERGY_CERTIFICATE" or undefined).
         * @param state redux state
         * @param parameter IDP energy certificate parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleIdpEnergyCertParam: (
            state,
            parameter: string,
            parameterType: RevaValueParameterType = "IDP_ENERGY_CERTIFICATE",
        ) => {
            if (parameterType !== "IDP_ENERGY_CERTIFICATE") return null;
            return state.idpEnergyCertificate?.[parameter] ?? null;
        },

        /**
         * Select single register parameter value (only if parameterType is "REGISTER" or undefined).
         * @param state redux state
         * @param parameter register parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleRegisterParam: (state, parameter: string, parameterType: RevaValueParameterType = "REGISTER") => {
            if (parameterType !== "REGISTER") return null;
            return state.register?.[parameter] ?? null;
        },

        /**
         * Select single Immodrop parameter value (only if parameterType is "IMMODROP" or undefined).
         * @param state redux state
         * @param parameter Immodrop parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleImmodropParam: (state, parameter: string, parameterType: RevaValueParameterType = "IMMODROP") => {
            if (parameterType !== "IMMODROP") return null;
            return state.immodrop?.[parameter] ?? null;
        },

        /**
         * Das Segment laut Exposé. Es liegt nicht als gewöhnlicher Parameter in der Schicht, sondern
         * unter dem technischen Schlüssel `_importSource` bzw. wird aus dem differenzierten Objekttyp
         * abgeleitet (#19159) — deshalb ein eigener Selektor statt selectSingleImmodropParam.
         */
        selectExposeSegment: (state) => readExposeSegment(state.immodrop),

        /**
         * Ob außer dem Override eine Quelle ein von REVA geführtes Segment trägt (#19444).
         *
         * Nur dann ist das Zurücksetzen des Overrides eine sinnvolle Handlung: Ohne zweite Quelle
         * fiele das Segment auf den technischen Anker, und der ist keine Entscheidung, die jemand
         * getroffen hat.
         */
        selectHasOtherSegmentSource: (state) =>
            isRevaSegment(state.initialValuationParams?.segment) ||
            isRevaSegment(state.inspectionParams?.segment) ||
            isRevaSegment(readExposeSegment(state.immodrop)),

        /**
         * The segments the sources below the override offer. Backs the buttons on the segment field
         * that resolve a conflict, like the source-value buttons of the number fields.
         */
        selectSegmentCandidates: (state) =>
            listSegmentCandidates({
                initialValuation: state.initialValuationParams?.segment,
                inspection: state.inspectionParams?.segment,
                immodrop: readExposeSegment(state.immodrop),
            }),

        /**
         * Select single inspection parameter value (only if parameterType is "AVM" or undefined).
         * @param state redux state
         * @param parameter inspection parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleInspectionParam: (state, parameter: string, parameterType: RevaValueParameterType = "AVM") => {
            if (parameterType !== "AVM") return null;
            return state.inspectionParams?.[parameter] ?? null;
        },

        /**
         * Select single inspection parameter value (only if parameterType is "AVM" or undefined).
         * @param state redux state
         * @param parameter inspection derived parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleInspectionDerivedParam: (
            state,
            parameter: string,
            parameterType: RevaValueParameterType = "INSPECTION_DERIVED",
        ) => {
            if (parameterType !== "INSPECTION_DERIVED") return null;
            return state.inspectionDerived?.[parameter] ?? null;
        },

        /**
         * Select single inspection parameter value (only if parameterType is "AVM" or undefined).
         * @param state redux state
         * @param parameter plot parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSinglePlotParam: (state, parameter: string, parameterType: RevaValueParameterType = "PLOT") => {
            if (parameterType !== "PLOT") return null;
            return state.plot?.[parameter] ?? null;
        },

        /**
         * Select single first finalize parameter value (only if parameterType is "AVM" or undefined).
         * @param state redux state
         * @param parameter plot parameter name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleFirstFinalizeParam: (
            state,
            parameter: string,
            parameterType: RevaValueParameterType = "FIRST_FINALIZE",
        ) => {
            if (parameterType !== "FIRST_FINALIZE") return null;
            return state.firstFinalizeParams?.[parameter] ?? null;
        },

        /**
         * Select single document property value (only if parameterType is "DOC_PROPERTY" or undefined).
         * @param state redux state
         * @param property document property name to query
         * @param parameterType parameter type; any other type yields null
         */
        selectSingleDocProperty: (state, property: string, parameterType: RevaValueParameterType = "DOC_PROPERTY") => {
            if (parameterType !== "DOC_PROPERTY") return null;
            return state.properties?.[property] ?? null;
        },

        /**
         * Select comment for a parameter
         * @param state redux state
         * @param parameter parameter name to query
         * @param parameterType parameter type; selects the matching comment layer
         */
        selectCommentForParam: (state, parameter: string, parameterType?: RevaValueParameterType) => {
            switch (parameterType) {
                case "DOC_PROPERTY":
                case "META":
                case "IDP_ENERGY_CERTIFICATE":
                    return null;
                case "AVM":
                    return state.comments?.[parameter] ?? null;
                case "ENERGY_CERT":
                    return state.energyComments?.[parameter] ?? null;
                case "REGISTER":
                    return state.registerComments?.[parameter] ?? null;
                case "INSPECTION_DERIVED":
                    return state.inspectionDerivedComments?.[parameter] ?? null;
                case "PLOT":
                    return state.plotComments?.[parameter] ?? null;
                default:
                    console.warn(
                        `selectCommentForParam: selectCommentForParam is undefined for parameter ${parameter} (ParameterType:${parameterType})`,
                    );
                    return null;
            }
        },

        selectAllCommentValues: (state) => {
            const allCommentKeys = new Set([
                ...Object.keys(state.comments || {}),
                ...Object.keys(state.energyComments || {}),
                ...Object.keys(state.registerComments || {}),
                ...Object.keys(state.inspectionDerivedComments || {}),
                ...Object.keys(state.plotComments || {}),
            ]);
            const result: Record<string, any> = {};

            allCommentKeys.forEach((key) => {
                result[key] = {
                    avm: state.comments?.[key] || null,
                    energy: state.energyComments?.[key] || null,
                    register: state.registerComments?.[key] || null,
                    inspectionDerived: state.inspectionDerivedComments?.[key] || null,
                    plot: state.plotComments?.[key] || null,
                };
            });
            return result;
        },

        selectAllValues: (state) => {
            const allValueKeys = new Set([
                ...Object.keys(state.params || {}),
                ...Object.keys(state.register || {}),
                ...Object.keys(state.energy || {}),
                ...Object.keys(state.plot || {}),
                ...Object.keys(state.inspectionDerived || {}),
                ...Object.keys(state.inspectionParams || {}),
                ...Object.keys(state.initialValuationParams || {}),
                ...Object.keys(state.firstFinalizeParams || {}),
                ...Object.keys(state.idpEnergyCertificate || {}),
                ...Object.keys(state.immodrop || {}),
            ]);
            const result: Record<string, any> = {};

            allValueKeys.forEach((key) => {
                result[key] = {
                    avm: state.params?.[key] ?? null,
                    energy: state.energy?.[key] ?? null,
                    register: state.register?.[key] ?? null,
                    plot: state.plot?.[key] ?? null,
                    inspectionDerived: state.inspectionDerived?.[key] ?? null,
                    inspection: state.inspectionParams?.[key] ?? null,
                    initialValuation: state.initialValuationParams?.[key] ?? null,
                    firstFinalize: state.firstFinalizeParams?.[key] ?? null,
                    idpEnergyCertificate: state.idpEnergyCertificate?.[key] ?? null,
                    immodrop: state.immodrop?.[key] ?? null,
                };
            });
            return result;
        },

        /**
         * Select single parameter error
         * @param state redux state
         * @param parameter parameter name to query
         */
        selectSingleParamError: (state, parameter: string) => {
            return state.paramErrors[parameter] || null;
        },

        selectParameterConflicts: (state) => state.parameterConflicts,
    },
});

export const {
    resetObjectParameters,
    setParamsForParameterType,
    updateParamsForParameterType,
    setSingleParam,
    setExternalValidationErrors,
    setParamErrors,
    setSegmentAndParameters,
    setInitialValuationAndInspectionParams,
    setSingleComment,
    setEditedValueParamErrors,
    setParameterConflict,
    clearAllParameterConflicts,
} = objectParametersSlice.actions;

export const {
    selectSegment,
    selectAllParams,
    selectAllMetaParams,
    selectAllDocProperties,
    selectAllEnergyParams,
    selectAllIdpEnergyCertParams,
    selectAllRegisterParams,
    selectAllImmodropParams,
    selectAllInspectionDerivedParams,
    selectAllPlotParams,
    selectAllParamErrors,
    selectAllInitialValuationParams,
    selectInitialValuationOutputParams,
    selectInitialValuationAdditionalProperties,
    selectAllInspectionParams,
    selectAllCommentValues,
    selectAllValues,
    selectSingleParam,
    selectSingleParamError,
    selectSingleAvmParam,
    selectSingleMetaParam,
    selectSingleInitialValuationParam,
    selectSingleInspectionParam,
    selectSingleInspectionDerivedParam,
    selectSinglePlotParam,
    selectSingleDocProperty,
    selectSingleEnergyParam,
    selectSingleIdpEnergyCertParam,
    selectSingleRegisterParam,
    selectSingleImmodropParam,
    selectExposeSegment,
    selectHasOtherSegmentSource,
    selectSegmentCandidates,
    selectCommentForParam,
    selectEditedValueParamErrors,
    selectParameterConflicts,
    selectHasSpfTable,
    selectAllFirstFinalizeParams,
    selectSingleFirstFinalizeParam,
} = objectParametersSlice.selectors;

export const ALL_SOURCES_TO_ROUTE = {
    avm: "/object/overview",
    firstFinalize: "/object/finalize",
    register: "/object/register",
    energy: "/object/energy",
    idpEnergyCertificate: "/object/energy",
    plot: "/object/groundvalue",
    inspectionDerived: "/object/inspection",
    immodrop: "/object/immodrop",
};

/**
 * Merge two objects, combining their values into arrays.
 * @return merged object
 */
const mergeErrors = (existingErrors: ObjectParamErrorsMap, newErrors: ObjectParamErrorsMap): ObjectParamErrorsMap =>
    Object.fromEntries(
        [...new Set([...Object.keys(existingErrors), ...Object.keys(newErrors)])].map((key) => [
            key,
            [...(existingErrors[key] || []), ...(newErrors[key] || [])],
        ]),
    );

export default objectParametersSlice.reducer;
